import { escapeHtml } from '../lib/html.js';
import { fmtNum } from '../lib/format.js';
import { listShopifyLojas, listShopifyVendas } from '../lib/shopify.js';
import { fmtValor, moedaBase } from '../lib/moeda.js';
import { localDateKey, addDays } from '../lib/dates.js';
import { PERIODOS } from '../data/sections.js';

const FUNIL_ETAPAS = [
  { key: 'pedidos', nome: 'Pedidos criados', escuro: '#567bb4', cor: '#71a3f4' },
  { key: 'pagos', nome: 'Pagos', escuro: '#a7893f', cor: '#f2c14b' },
  { key: 'enviados', nome: 'Enviados', escuro: '#458852', cor: '#5ec16a' },
  { key: 'concluidos', nome: 'Concluídos', escuro: '#9a3f40', cor: '#dd524c' },
];

const SETA_FN = '<svg class="fn-arrow" viewBox="0 0 12 5" aria-hidden="true"><path d="M0 2.5H11.2M8.8 0.4L11.4 2.5L8.8 4.6" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round"/></svg><span class="sr-only">para</span>';
const pctFn = (v) => `${(Number(v) || 0).toFixed(2)}%`;

let vendas = [];
let lojas = [];
let loaded = false;
let loadedKey = '';
let loading = false;
let loadError = '';
const filters = { loja: '', periodo: { tipo: '30d' } };

export function setFunilFilter(name, value) {
  if (name === 'periodo') {
    filters.periodo = value && typeof value === 'object' ? value : { tipo: value || '30d' };
    loaded = false;
    return;
  }
  if (name === 'de' || name === 'ate') {
    filters.periodo = { tipo: 'custom', de: name === 'de' ? value : filters.periodo.de, ate: name === 'ate' ? value : filters.periodo.ate };
    loaded = false;
    return;
  }
  filters[name] = value || '';
  loaded = false;
}

export function invalidateFunil() {
  loaded = false;
}

function periodoRange(per = filters.periodo) {
  const hoje = localDateKey();
  let de;
  let ate;
  switch (per.tipo) {
    case 'ontem':
      de = ate = addDays(hoje, -1);
      break;
    case '7d':
      de = addDays(hoje, -6);
      ate = hoje;
      break;
    case '30d':
      de = addDays(hoje, -29);
      ate = hoje;
      break;
    case '90d':
      de = addDays(hoje, -89);
      ate = hoje;
      break;
    case 'custom':
      de = per.de || hoje;
      ate = per.ate || de;
      if (ate > hoje) ate = hoje;
      if (de > ate) [de, ate] = [ate, de];
      break;
    default:
      de = ate = hoje;
  }
  return { de, ate };
}

function cacheKey() {
  const { de, ate } = periodoRange();
  return [moedaBase(), filters.loja || '', de, ate].join('|');
}

function dataLocalPedido(iso) {
  if (!iso) return '';
  const dt = new Date(iso);
  if (!Number.isNaN(dt.getTime())) return localDateKey(dt);
  return String(iso).slice(0, 10);
}

function noPeriodo(venda, de, ate) {
  const key = dataLocalPedido(venda.created_at_shopify);
  return Boolean(key) && key >= de && key <= ate;
}

export async function refreshFunil() {
  loading = true;
  loadError = '';
  const { de, ate } = periodoRange();
  try {
    const params = { limit: '500', moeda: moedaBase(), from: addDays(de, -1), to: addDays(ate, 1) };
    if (filters.loja) params.loja = filters.loja;
    const [vendasData, lojasData] = await Promise.all([
      listShopifyVendas(params),
      listShopifyLojas(),
    ]);
    vendas = vendasData.filter((venda) => noPeriodo(venda, de, ate));
    lojas = lojasData.lojas || [];
    loadedKey = cacheKey();
    loaded = true;
  } catch (err) {
    loadError = err.message || 'Não foi possível carregar o funil das lojas.';
    vendas = [];
    lojas = [];
    loaded = true;
    loadedKey = cacheKey();
  } finally {
    loading = false;
  }
  return vendas;
}

function isPago(venda) {
  const status = String(venda.financial_status || '').toLowerCase();
  return (status === 'paid' || status === 'partially_paid' || status === 'partially_refunded') && !venda.cancelled_at;
}

function isEnviado(venda) {
  return String(venda.fulfillment_status || '').toLowerCase() === 'fulfilled';
}

function isConcluido(venda) {
  return isPago(venda) && isEnviado(venda) && String(venda.financial_status || '').toLowerCase() === 'paid';
}

function valorBase(venda) {
  if (venda.total_base != null) return Number(venda.total_base) || 0;
  if (venda.total_brl != null && moedaBase() === 'BRL') return Number(venda.total_brl) || 0;
  if (String(venda.currency || '').toUpperCase() === moedaBase()) return Number(venda.total) || 0;
  return 0;
}

function dadosFunil(lista) {
  const pagos = lista.filter(isPago);
  const enviados = pagos.filter(isEnviado);
  const concluidos = enviados.filter(isConcluido);
  const valores = {
    pedidos: lista.length,
    pagos: pagos.length,
    enviados: enviados.length,
    concluidos: concluidos.length,
  };
  const vendido = pagos.reduce((sum, item) => sum + valorBase(item), 0);
  return {
    etapas: FUNIL_ETAPAS.map((etapa) => ({ ...etapa, valor: valores[etapa.key] })),
    vendido,
    ticket: pagos.length ? vendido / pagos.length : 0,
    truncado: lista.length >= 500,
  };
}

export function lerFunil() {
  return dadosFunil(vendas);
}

export function renderFunil({ onNeedRefresh } = {}) {
  if (loaded && loadedKey !== cacheKey()) loaded = false;
  if (!loaded && !loading) {
    refreshFunil().then(() => onNeedRefresh?.());
  }
  if (!loaded || loading) {
    return `<div class="empty-state">Carregando funil das lojas…</div>`;
  }
  if (loadError) {
    return `<div class="empty-state">${escapeHtml(loadError)}</div>`;
  }

  const { de, ate } = periodoRange();
  const dados = dadosFunil(vendas);
  const etapas = dados.etapas;
  const semDados = etapas[0].valor === 0;
  const lojaOpts = lojas.length
    ? lojas
      .map((loja) => ({
        value: loja.shop_domain,
        label: loja.nome && loja.nome !== loja.shop_domain ? `${loja.nome} · ${loja.shop_domain}` : loja.shop_domain,
      }))
      .filter((item) => item.value)
      .sort((a, b) => a.label.localeCompare(b.label, 'pt-BR'))
    : [...new Set(vendas.map((item) => item.shop_domain).filter(Boolean))]
      .sort()
      .map((value) => ({ value, label: value }));

  if (filters.loja && !lojaOpts.some((item) => item.value === filters.loja)) {
    filters.loja = '';
  }

  const toolbar = `
    <div class="table-toolbar">
      <div style="display:flex;gap:8px;flex-wrap:wrap">
        <select class="filter" id="funil-periodo" aria-label="Período">
          ${PERIODOS.map(([value, label]) => `<option value="${value}" ${filters.periodo.tipo === value ? 'selected' : ''}>${escapeHtml(label)}</option>`).join('')}
        </select>
        ${filters.periodo.tipo === 'custom' ? `
          <input class="filter" type="date" id="funil-de" aria-label="De" max="${localDateKey()}" value="${escapeHtml(de)}">
          <input class="filter" type="date" id="funil-ate" aria-label="Até" max="${localDateKey()}" value="${escapeHtml(ate)}">
        ` : ''}
        <select class="filter" id="funil-loja" aria-label="Loja">
          <option value="">Todas as lojas</option>
          ${lojaOpts.map((loja) => `<option value="${escapeHtml(loja.value)}" ${loja.value === filters.loja ? 'selected' : ''}>${escapeHtml(loja.label)}</option>`).join('')}
        </select>
      </div>
      <span class="cell-sub">${semDados
        ? 'Aguardando vendas da Shopify neste período'
        : `${fmtNum(etapas[0].valor)} pedido(s) · ${escapeHtml(de === ate ? de : `${de} a ${ate}`)}`}</span>
    </div>`;

  const n = etapas.length;
  const topo = etapas[0].valor;
  const taxa = (i) => {
    if (i === 0) return topo > 0 ? 100 : 0;
    return etapas[i - 1].valor > 0 ? (etapas[i].valor / etapas[i - 1].valor) * 100 : 0;
  };
  const dot = (e) => `<i style="background:var(--fn-c-${e.key});"></i>`;
  const grad = (e) => `linear-gradient(90deg,${e.escuro},${e.cor})`;

  const visual = etapas.map((e, i) => {
    const ultima = i === n - 1;
    const largura = n > 1 ? 100 - i * (80 / (n - 1)) : 100;
    const fundo = i === 0 ? e.cor : grad(e);
    return `
      <div class="fnv-item${ultima && n > 1 ? ' last' : ''}">
        <div class="fnv-bar" style="width:${largura.toFixed(3)}%;background:${fundo};">${i === 0 ? (topo > 0 ? '100%' : '0%') : pctFn(taxa(i))}</div>
        <div class="fnv-row"><span class="fn-name">${dot(e)}${e.nome}</span><span class="fn-num">${fmtNum(e.valor)}</span></div>
        ${ultima ? '' : `<div class="fnv-rate">Taxa de conversão: ${pctFn(taxa(i))}</div>`}
      </div>`;
  }).join('');

  const detalhes = etapas.map((e, i) => {
    const ant = etapas[i - 1];
    const perda = i > 0 ? Math.max(0, ant.valor - e.valor) : 0;
    const perdaPct = i > 0 && ant.valor > 0 ? (perda / ant.valor) * 100 : 0;
    return `
      <div class="fnd-item">
        <div class="fnd-head"><span class="fn-name">${dot(e)}${e.nome}</span><span class="fn-num">${fmtNum(e.valor)}</span></div>
        <div class="fnd-track">${taxa(i) > 0 ? `<div class="fnd-fill" style="width:${Math.min(100, taxa(i)).toFixed(3)}%;background:${grad(e)};"></div>` : ''}</div>
        ${i > 0 ? `<div class="fnd-foot">
          <span style="color:var(--fn-c-${e.key});">${ant.nome}${SETA_FN}${e.nome}: ${pctFn(taxa(i))}</span>
          <span class="loss">Perda: ${fmtNum(perda)} (${pctFn(perdaPct)})</span>
        </div>` : ''}
      </div>`;
  }).join('');

  const fim = etapas[n - 1];
  const total = topo > 0 ? (fim.valor / topo) * 100 : 0;
  const nota = semDados
    ? `<div class="funnel-note">Sem pedidos neste recorte. Conecte a loja em Plataformas, sincronize o histórico e escolha o período.</div>`
    : dados.truncado
      ? `<div class="funnel-note">O recorte chegou no limite de 500 pedidos. Escolha uma loja ou um período menor para ver o funil completo.</div>`
      : '';

  return `
    <div class="fat-stats" style="margin-bottom:16px;flex-wrap:wrap">
      <div class="mini-stat">
        <div class="lbl">Pedidos</div>
        <div class="val">${fmtNum(etapas[0].valor)}</div>
        <div class="cell-sub">${fmtNum(etapas[1].valor)} pago(s)</div>
      </div>
      <div class="mini-stat">
        <div class="lbl">Vendido pago (${escapeHtml(moedaBase())})</div>
        <div class="val">${escapeHtml(fmtValor(dados.vendido))}</div>
        <div class="cell-sub">${fmtNum(etapas[2].valor)} enviado(s)</div>
      </div>
      <div class="mini-stat">
        <div class="lbl">Ticket médio</div>
        <div class="val">${escapeHtml(fmtValor(dados.ticket))}</div>
        <div class="cell-sub">${fmtNum(etapas[3].valor)} concluído(s)</div>
      </div>
    </div>
    ${toolbar}
    <div class="funnel-grid">
      <div class="fn-card fn-visual">
        <h3 class="fn-title">Visualização do Funil</h3>
        <p class="fn-sub">Pedidos das lojas Shopify até o envio concluído</p>
        <div class="fnv">${visual}</div>
        ${semDados ? '' : nota}
      </div>
      <div class="fn-card fn-detail">
        <h3 class="fn-title">Detalhes do Funil</h3>
        <p class="fn-sub">Conversão entre pedido, pagamento, envio e pedido concluído</p>
        <div class="fnd">${detalhes}</div>
        <div class="fnd-total">
          <div class="fnd-total-head"><span>Taxa de Conversão Total</span><b>${pctFn(total)}</b></div>
          <div class="fnd-track">${total > 0 ? `<div class="fnd-fill" style="width:${Math.min(100, total).toFixed(3)}%;"></div>` : ''}</div>
          <div class="fnd-cap">${fmtNum(fim.valor)} concluídos de ${fmtNum(topo)} pedidos criados</div>
        </div>
        ${semDados ? nota : ''}
      </div>
    </div>`;
}
